import { Card, IconButton, LuIcon, Text } from "@overmap-ai/blocks"
import { OrganizationAccessLevel, UNTITLED_ISSUE_TITLE } from "@overmap-ai/common"
import { selectIssueById } from "@overmap-ai/core"
import { cx } from "class-variance-authority"
import { type ComponentProps, type ComponentPropsWithRef, memo, type MouseEventHandler, useCallback } from "react"

import { interactiveCardCva } from "../../cva"
import { modelIconMapping } from "../../mappings/icons"
import { useAppSelector } from "../../store"
import { IssueTypeIcon } from "../Icons/IssueTypeIcon"
import { IssueIndexLabel } from "../IndexLabels/IssueIndex"
import { IssueLink } from "../Links"
import { OrganizationAccessRequired } from "../OrganizationAccessRequired/AccessRequired"

interface IssueAssociationIssueListItemProps extends ComponentPropsWithRef<"div"> {
	issueId: string
	onDelete: () => void
	size?: ComponentProps<typeof Card>["size"]
}

export const IssueAssociationIssueListItem = memo<IssueAssociationIssueListItemProps>((props) => {
	const { className, issueId, onDelete, size = "sm", ...rest } = props

	const issue = useAppSelector(selectIssueById(issueId))

	if (!issue) {
		throw new Error(`Issue with id ${issueId} not found in store.`)
	}

	const handleDelete = useCallback<MouseEventHandler<HTMLButtonElement>>(
		(e) => {
			e.stopPropagation()
			onDelete?.()
		},
		[onDelete],
	)

	return (
		<Card
			className={cx(className, "flex cursor-pointer justify-between gap-2", interactiveCardCva())}
			role="listitem"
			size={size}
			{...rest}
		>
			<IssueLink className="flex min-w-0 grow items-center gap-2" issueId={issue.uuid}>
				<IssueTypeIcon issueTypeId={issue.issue_type} />
				<IssueIndexLabel issueId={issue.uuid} className="shrink-0 whitespace-nowrap" size={size} />
				<Text accentColor="base" className="truncate" size={size}>
					{issue.title || UNTITLED_ISSUE_TITLE}
				</Text>
			</IssueLink>
			<div className="flex items-center justify-center pl-0">
				<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.ADMIN} type="hide">
					<IconButton
						aria-label="delete"
						accentColor="base"
						size={size}
						variant="ghost"
						onClick={handleDelete}
					>
						<LuIcon icon={modelIconMapping.close} />
					</IconButton>
				</OrganizationAccessRequired>
			</div>
		</Card>
	)
})
IssueAssociationIssueListItem.displayName = "IssueAssociationIssueListItem"
